// ============================================================
//  src/admin/pages/AdminBotsPage.js
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { listBots, getQueueSettings, saveQueueSettings } from '../api/adminClient';
import { PageHeader, Badge, Pagination, isFlagTrue } from '../components/UI';
import DataTable from '../components/DataTable';
import { useConfirm } from '../components/ConfirmModal';

function statusBadge(b) {
  if (isFlagTrue(b.bAdminSuspended)) return <Badge tone="danger">Suspended</Badge>;
  if (isFlagTrue(b.bIsActive)) return <Badge tone="success">Active</Badge>;
  return <Badge tone="muted">Inactive</Badge>;
}

function queueBadge(b) {
  // bQueueMode: null/undefined = inherits the platform default, 1 = forced
  // on for this bot, 0 = forced off regardless of the platform default.
  if (b.bQueueMode === 1 || b.bQueueMode === '1') return <Badge tone="primary">On</Badge>;
  if (b.bQueueMode === 0 || b.bQueueMode === '0') return <Badge tone="muted">Off</Badge>;
  return <Badge tone="muted">Auto</Badge>;
}

function QueueSettingsCard() {
  const confirm = useConfirm();
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    getQueueSettings().then(res => setSettings(res.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleToggle() {
    const turningOn = !settings.global_enabled;
    const ok = await confirm({
      title: turningOn ? 'Enable queue mode platform-wide?' : 'Disable queue mode platform-wide?',
      message: turningOn
        ? "New incoming messages for any bot set to \"Auto\" will be queued and processed by the background worker instead of replied to instantly. Make sure cron/process_queue.php is running before enabling this."
        : 'Bots set to "Auto" will go back to replying synchronously, in real time.',
      confirmText: turningOn ? 'Enable' : 'Disable',
      tone: turningOn ? 'primary' : 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await saveQueueSettings(turningOn);
      toast.success('Queue mode setting saved');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not save setting');
    } finally { setBusy(false); }
  }

  if (!settings) return null;

  const { global_enabled, bot_counts, queue_depth } = settings;
  const hasBacklog = (queue_depth?.pending || 0) > 0 || (queue_depth?.failed || 0) > 0;

  return (
    <div className="bf-card">
      <div className="bf-card-hdr">Queue Mode</div>
      <div className="bf-card-body">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ fontSize: 13.5 }}>
            Platform-wide default:{' '}
            {global_enabled ? <Badge tone="primary">ON</Badge> : <Badge tone="muted">OFF</Badge>}
            <div className="bf-muted" style={{ marginTop: 4 }}>
              Applies to bots left on "Auto" below. {bot_counts.forced_on} bot(s) forced on,{' '}
              {bot_counts.forced_off} forced off, {bot_counts.auto} on auto.
            </div>
          </div>
          <button
            className={global_enabled ? 'bf-btn-danger-outline' : 'btn btn-primary'}
            disabled={busy}
            onClick={handleToggle}
          >
            {global_enabled ? 'Disable Platform-Wide' : 'Enable Platform-Wide'}
          </button>
        </div>

        {hasBacklog && (
          <div className="bf-muted" style={{ marginTop: 12, fontSize: 13 }}>
            Queue backlog — pending: {queue_depth.pending}, failed: {queue_depth.failed}.
            {queue_depth.failed > 0 && ' Failed messages likely mean the cron worker (cron/process_queue.php) isn\u2019t running — check queue.log.'}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminBotsPage() {
  const [bots, setBots] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const limit = 25;

  const load = useCallback(() => {
    setLoading(true);
    listBots({ page, limit, search })
      .then(res => { setBots(res.data.bots); setTotal(res.data.total); })
      .catch(err => toast.error(err?.response?.data?.message || 'Could not load bots'))
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { load(); }, [load]);
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const columns = [
    {
      key: 'bot', label: 'Bot', primary: true,
      render: b => (
        <div>
          <div className="bf-cell-strong">{b.bName}</div>
          <div className="bf-cell-muted bf-mono">{b.bId}</div>
        </div>
      ),
    },
    {
      key: 'owner', label: 'Owner',
      render: b => (
        <div>
          <div>{b.sFname} {b.sLname}</div>
          <div className="bf-cell-muted">{b.sEmail}</div>
        </div>
      ),
    },
    { key: 'users', label: 'Users', render: b => b.totalUsers },
    { key: 'commands', label: 'Commands', render: b => b.totalCommands },
    { key: 'queue', label: 'Queue', render: queueBadge },
    { key: 'status', label: 'Status', render: statusBadge },
  ];

  return (
    <div className="bf-page">
      <PageHeader title="Bots" subtitle={`${total.toLocaleString('en-NG')} bots across all subscribers`} />

      <QueueSettingsCard />

      <div className="bf-toolbar">
        <input
          className="bf-input"
          placeholder="Search by bot name, ID, or owner email…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
      </div>

      <div className="bf-card">
        <div className="bf-card-body-flush">
          <DataTable
            columns={columns}
            rows={bots}
            keyField="bId"
            onRowClick={b => navigate(`/bot-father/bots/${b.bId}`)}
            loading={loading}
            loadingMessage="Loading bots…"
            emptyMessage="No bots found."
          />
        </div>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={setPage} total={total} limit={limit} />
    </div>
  );
}